import React, { useMemo, useCallback, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  IconButton,
  Flex,
  Progress,
  Badge,
  HStack,
  Button,
  Tooltip,
  useToast,
  Radio,
  RadioGroup,
  Icon,
} from "@chakra-ui/react";
import {
  SelectedSupplier,
  PartQuantity,
  calculateRemainingParts,
} from "./utils/partsCalculationUtils";
import {
  CPTSlab,
  calculateCPTSlabsForSuppliers,
  DEFAULT_HLD_DISTANCE_METRES,
} from "./utils/cptSlabUtils";
import {
  MdClose,
  MdLocationOn,
  MdDeleteForever,
  MdInfoOutline,
} from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import { FaShareAlt, FaWhatsapp } from "react-icons/fa";
import { useBucketMethods } from "./utils/bucketUtils";
import SavedBucketDetailsModal from "./SavedBucketDetailsModal";
import { IBucket } from "../../views/Orders/store/useBucketsStore";
import { BucketType } from "./types";
import { FiLogOut } from "react-icons/fi";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";
import { useSupplierCPTSlabs } from "./hooks/useSupplierCPTSlabs";
import { CPTSlabSelector } from "./components/CPTSlabSelector";
import {
  calculateSupplierPrice,
  calculateDeliveryFee,
} from "./utils/priceAndDeliveryUtils";
import {
  parseCptHoursOffset,
  calculateCPTForSlab,
  CPTHoursOffsetBySlab,
} from "./utils/cptCalculationUtils";
import {
  EstimateSplitDeliveryType,
  SelectedDeliveryOption,
} from "../DeliveryOptions";
import { isEnquiryWhitelistedForLatestEstimateUi } from "../DeliveryOptions/utils/isEnquiryWhitelistedForLatestEstimateUi";
import { useOrdersStore } from "../../views/Orders/store/useOrdersStore";
import services from "../../views/Orders/services";
import { BsEyeFill } from "react-icons/bs";
import { PartConfirmationBucketPanel } from "./PartConfirmationBucketPanel";
import { PartConfirmationBucketEntry } from "./utils/partsCalculationUtils";
import { PartConfirmationSendResult } from "../../views/Orders/hooks/useSendPartConfirmationBucket";

const DISABLE_DOWNLOAD_PART_AVAILABLITY = true;

interface SelectedSuppliersPanelProps {
  selectedSuppliers: SelectedSupplier[];
  onRemoveSupplier: (supplierId: string) => void;
  onClearAll: () => void;
  isFullyFulfilled: boolean;
  fulfillmentPercentage: number;
  orderNumber: string;
  originalParts: PartQuantity[];
  orderData: any;
  handleExecuteBucket: (selectedSuppliers: SelectedSupplier[]) => void;
  handleExecuteBucketsWithDeliveryTypes?: (
    buckets: Array<{
      suppliers: SelectedSupplier[];
      deliveryType: EstimateSplitDeliveryType;
    }>,
  ) => void;
  showCreateAnotherBucket: boolean;
  remainingPartsToFulfill: PartQuantity[];
  isFetchingDeliveryOptions?: boolean;
  isEstimateOrder: boolean;
  onDownloadBucketPdf?: () => void | Promise<void>;
  isDownloadBucketPdfDisabled?: boolean;
  onShareDraftEstimate?: () => void | Promise<void>;
  isSharingDraftEstimate?: boolean;
  isShareDraftEstimateDisabled?: boolean;
  onSendBucketPdfOnWhatsApp?: () => void | Promise<void>;
  isSendingBucketPdfOnWhatsApp?: boolean;
  isSendBucketPdfOnWhatsAppDisabled?: boolean;
  isGenerateEstimateBlockedByPartConfirmation?: boolean;
  partConfirmationBlockReason?: string;
  partConfirmationBucket?: PartConfirmationBucketEntry[];
  onRemovePartConfirmationSupplier?: (supplierId: string) => void;
  onSendPartConfirmation?: () => void;
  isSendingPartConfirmation?: boolean;
  isSendPartConfirmationEnabled?: boolean;
  sendPartConfirmationDisabledReason?: string;
  partConfirmationSendResults?: PartConfirmationSendResult[];
  partConfirmationNonRemovableSupplierIds?: string[];
}

export const SelectedSuppliersPanel: React.FC<SelectedSuppliersPanelProps> = ({
  selectedSuppliers,
  onRemoveSupplier,
  isFullyFulfilled,
  fulfillmentPercentage,
  orderNumber,
  originalParts,
  orderData,
  handleExecuteBucket,
  handleExecuteBucketsWithDeliveryTypes,
  showCreateAnotherBucket,
  remainingPartsToFulfill,
  isFetchingDeliveryOptions = false,
  isEstimateOrder,
  onDownloadBucketPdf,
  isDownloadBucketPdfDisabled = false,
  onShareDraftEstimate,
  isSharingDraftEstimate = false,
  isShareDraftEstimateDisabled = false,
  onSendBucketPdfOnWhatsApp,
  isSendingBucketPdfOnWhatsApp = false,
  isSendBucketPdfOnWhatsAppDisabled = false,
  isGenerateEstimateBlockedByPartConfirmation = false,
  partConfirmationBlockReason = "",
  partConfirmationBucket = [],
  onRemovePartConfirmationSupplier,
  onSendPartConfirmation,
  isSendingPartConfirmation = false,
  isSendPartConfirmationEnabled = false,
  sendPartConfirmationDisabledReason = "No new quantity to send",
  partConfirmationSendResults,
  partConfirmationNonRemovableSupplierIds = [],
}) => {
  const { getBucketsByOrderNumber, deleteBucketByBucketId } =
    useBucketMethods();
  const { data: mobileConfigs } = useGetOrderMediaConfig();

  const bucketsForCurrentOrder = getBucketsByOrderNumber(orderNumber);
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  const [activeBucket, setActiveBucket] = React.useState<IBucket | null>(null);
  const [bucketDeliveryTypes, setBucketDeliveryTypes] = React.useState<
    Record<string, EstimateSplitDeliveryType>
  >({});

  const isLatestEstimateUiWhitelisted = useMemo(
    () =>
      isEnquiryWhitelistedForLatestEstimateUi(
        (
          mobileConfigs as {
            whitelisted_enquiry_nos_for_latest_estimate_ui?: unknown;
          }
        )?.whitelisted_enquiry_nos_for_latest_estimate_ui,
        orderNumber,
        orderData,
      ),
    [mobileConfigs, orderNumber, orderData],
  );

  const showDualBucketDeliveryTypeUi =
    isEstimateOrder &&
    isLatestEstimateUiWhitelisted &&
    bucketsForCurrentOrder.length === 2;

  const bucketIdsKey = bucketsForCurrentOrder.map((b) => b.id).join("|");

  useEffect(() => {
    if (!showDualBucketDeliveryTypeUi) {
      setBucketDeliveryTypes({});
      return;
    }
    const [first, second] = bucketsForCurrentOrder;
    setBucketDeliveryTypes({
      [first.id]: "FASTEST",
      [second.id]: "CHEAPEST",
    });
    // Intentionally keyed by bucket id list — reset defaults when the pair changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDualBucketDeliveryTypeUi, bucketIdsKey]);

  const handleBucketDeliveryTypeChange = useCallback(
    (bucketId: string, deliveryType: EstimateSplitDeliveryType) => {
      if (!showDualBucketDeliveryTypeUi) return;
      const otherBucket = bucketsForCurrentOrder.find((b) => b.id !== bucketId);
      if (!otherBucket) return;
      const opposite: EstimateSplitDeliveryType =
        deliveryType === "FASTEST" ? "CHEAPEST" : "FASTEST";
      setBucketDeliveryTypes({
        [bucketId]: deliveryType,
        [otherBucket.id]: opposite,
      });
    },
    [showDualBucketDeliveryTypeUi, bucketsForCurrentOrder],
  );

  const orderItemsForOrder = useOrdersStore(
    useCallback((state) => state.orderItems[orderData._id], [orderData._id]),
  );

  const toast = useToast();
  const hldDistanceMetres =
    Number(mobileConfigs?.hldLocaleDistanceMetres) ||
    DEFAULT_HLD_DISTANCE_METRES;

  const handleGlobalViewEstimateClick = useCallback(() => {
    if (
      !showDualBucketDeliveryTypeUi ||
      !handleExecuteBucketsWithDeliveryTypes
    ) {
      return;
    }

    // Build each bucket independently — fastest/cheapest are alternative
    // fulfillment paths for the same order parts, not complementary splits.
    const bucketsWithTypes: Array<{
      suppliers: SelectedSupplier[];
      deliveryType: EstimateSplitDeliveryType;
    }> = [];

    for (const bucket of bucketsForCurrentOrder) {
      const deliveryType = bucketDeliveryTypes[bucket.id];
      if (!deliveryType) continue;
      const suppliers = calculateCPTSlabsForSuppliers(
        orderData,
        bucket.suppliers,
        hldDistanceMetres,
      );
      if (suppliers.length === 0) continue;
      bucketsWithTypes.push({ suppliers, deliveryType });
    }

    if (bucketsWithTypes.length === 0) return;
    handleExecuteBucketsWithDeliveryTypes(bucketsWithTypes);
  }, [
    showDualBucketDeliveryTypeUi,
    handleExecuteBucketsWithDeliveryTypes,
    bucketsForCurrentOrder,
    bucketDeliveryTypes,
    orderData,
    hldDistanceMetres,
  ]);

  const minDeliveryFee =
    Number(mobileConfigs?.logistics_min_delivery_fee_rs) || 30;
  const perKmPrice =
    Number(mobileConfigs?.logistics_delivery_fee_per_km_rs) || 13;
  const cptHoursOffsetBySlab = useMemo(
    () => parseCptHoursOffset(mobileConfigs?.logistics_cpt_hours_offset),
    [mobileConfigs?.logistics_cpt_hours_offset],
  );

  const {
    supplierCPTSlabs,
    getSuggestedCPTSlab,
    handleCPTSlabChange,
    getSuppliersWithCPTSlab,
  } = useSupplierCPTSlabs({
    selectedSuppliers,
    orderData,
    hldDistanceMetres,
  });

  const supplierCPTs = useMemo(() => {
    if (!selectedSuppliers.length) return new Map<string, Date | null>();

    const cpts = new Map<string, Date | null>();
    const orderEffectiveAt = orderData?.orderEffectiveAt;

    for (const supplier of selectedSuppliers) {
      const slab = supplierCPTSlabs.get(supplier.supplierId) || CPTSlab.OUTSIDE;
      cpts.set(
        supplier.supplierId,
        calculateCPTForSlab(
          orderEffectiveAt,
          slab as CPTSlab,
          cptHoursOffsetBySlab,
        ),
      );
    }

    return cpts;
  }, [
    selectedSuppliers,
    orderData?.orderEffectiveAt,
    supplierCPTSlabs,
    cptHoursOffsetBySlab,
  ]);

  const supplierPrices = useMemo(() => {
    if (!selectedSuppliers.length) return new Map<string, number | null>();

    const prices = new Map<string, number | null>();
    const orderItems = orderData?.orderItems || [];

    for (const supplier of selectedSuppliers) {
      prices.set(
        supplier.supplierId,
        calculateSupplierPrice(
          orderItems,
          supplier,
          selectedSuppliers,
          originalParts,
        ),
      );
    }

    return prices;
  }, [selectedSuppliers, orderData?.orderItems, originalParts]);

  const deliveryFees = useMemo(() => {
    if (!selectedSuppliers.length) return new Map<string, number | null>();

    const fees = new Map<string, number | null>();
    for (const supplier of selectedSuppliers) {
      const slab = supplierCPTSlabs.get(supplier.supplierId);
      fees.set(
        supplier.supplierId,
        calculateDeliveryFee(
          supplier.distanceKm,
          perKmPrice,
          minDeliveryFee,
          slab,
        ),
      );
    }
    return fees;
  }, [selectedSuppliers, perKmPrice, minDeliveryFee, supplierCPTSlabs]);

  const getDisableReason = (orderData: any, hasSelectedSuppliers: boolean) => {
    if (!hasSelectedSuppliers) return "Select at least one supplier";
    if (!orderData?.orderETA?.length) return "ETA is missing for this order";
    if (!orderData?.orderHandler?.length) return "Order handler not assigned";
    if (isGenerateEstimateBlockedByPartConfirmation) {
      return (
        partConfirmationBlockReason || "Waiting for part confirmation responses"
      );
    }
    return "";
  };

  const hasSelectedSuppliers = selectedSuppliers.length > 0;

  const isDisabled =
    !hasSelectedSuppliers ||
    !orderData?.orderETA?.length ||
    !orderData?.orderHandler?.length ||
    isGenerateEstimateBlockedByPartConfirmation;

  const disableReason = getDisableReason(orderData, hasSelectedSuppliers);

  const handleExecuteBucketClick = useCallback(() => {
    const suppliersWithSlab = getSuppliersWithCPTSlab();
    handleExecuteBucket(suppliersWithSlab);
  }, [getSuppliersWithCPTSlab, handleExecuteBucket]);

  // Handle delivery options refetch

  return (
    <Box
      borderColor="gray.200"
      overflowY="auto"
      zIndex={10}
      fontFamily="'Nunito','Inter', sans-serif"
      borderRadius="xl"
      ml={-4}
    >
      <VStack spacing={4} py={4} px={4} align="stretch">
        <Box bg="white" borderRadius="xl" p={4} boxShadow="md">
          <Text
            fontSize="1.25rem"
            fontWeight="bold"
            color="gray.800"
            mb={2}
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Order Progress
          </Text>
          <Box textAlign="center" mb={4}>
            <Text
              fontSize="1.75rem"
              fontWeight={900}
              color="#3B82F6"
              fontFamily="'Nunito','Inter', sans-serif"
            >
              {fulfillmentPercentage.toFixed(0)}%
            </Text>
            <Text
              fontSize="0.85rem"
              color="gray.600"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight={700}
            >
              {isFullyFulfilled ? "Complete" : "In Progress"}
            </Text>
          </Box>
          <Box mb={4}>
            <Progress
              value={fulfillmentPercentage}
              size="md"
              borderRadius="full"
              bg="gray.200"
              transition="all 0.3s ease"
              sx={{
                "& > div": {
                  background:
                    "linear-gradient(90deg,rgb(74, 141, 250),rgb(30, 195, 140))",
                  borderRadius: "full",
                  transition: "width 0.3s ease",
                },
              }}
            />
          </Box>
          <Flex gap={2} mt={1}>
            {onDownloadBucketPdf ? (
              <Button
                flex={1}
                minW={0}
                px={2}
                py={1.5}
                h="auto"
                minH={8}
                whiteSpace="normal"
                lineHeight="short"
                iconSpacing={1}
                colorScheme="blue"
                variant="solid"
                size="sm"
                leftIcon={<FiDownload />}
                onClick={() => {
                  void Promise.resolve(onDownloadBucketPdf()).catch(() => {
                    toast({
                      description: "Failed to generate PDF. Please try again.",
                      status: "error",
                    });
                  });
                }}
                isDisabled={
                  DISABLE_DOWNLOAD_PART_AVAILABLITY ||
                  isDownloadBucketPdfDisabled
                }
                fontFamily="'Nunito','Inter', sans-serif"
                fontSize="xs"
                fontWeight="600"
                bg="#4b85f2"
                _hover={{ bg: "#3a6fd9" }}
                _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              >
                Availability Report
              </Button>
            ) : null}
            {onShareDraftEstimate ? (
              <Button
                flex={1}
                minW={0}
                px={2}
                py={1.5}
                h="auto"
                minH={8}
                whiteSpace="normal"
                lineHeight="short"
                iconSpacing={1}
                colorScheme="green"
                variant="solid"
                size="sm"
                leftIcon={<FaShareAlt />}
                onClick={() => {
                  if (isSharingDraftEstimate) return;
                  void Promise.resolve(onShareDraftEstimate()).catch(() => {
                    toast({
                      description:
                        "Failed to share draft estimate. Please try again.",
                      status: "error",
                    });
                  });
                }}
                isLoading={isSharingDraftEstimate}
                loadingText="Sharing..."
                isDisabled={isShareDraftEstimateDisabled}
                fontFamily="'Nunito','Inter', sans-serif"
                fontSize="xs"
                fontWeight="600"
                bg="#25D366"
                _hover={{ bg: "#1ebe5b" }}
                _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              >
                Share Draft Estimate
              </Button>
            ) : onSendBucketPdfOnWhatsApp ? (
              <Button
                flex={1}
                minW={0}
                px={2}
                py={1.5}
                h="auto"
                minH={8}
                whiteSpace="normal"
                lineHeight="short"
                iconSpacing={1}
                colorScheme="green"
                variant="solid"
                size="sm"
                leftIcon={<FaWhatsapp />}
                onClick={() => {
                  if (isSendingBucketPdfOnWhatsApp) return;
                  void Promise.resolve(onSendBucketPdfOnWhatsApp()).catch(
                    () => {
                      toast({
                        description:
                          "Failed to send on WhatsApp. Please try again.",
                        status: "error",
                      });
                    },
                  );
                }}
                isLoading={isSendingBucketPdfOnWhatsApp}
                loadingText="Sending..."
                isDisabled={
                  isSendBucketPdfOnWhatsAppDisabled ||
                  isDownloadBucketPdfDisabled
                }
                fontFamily="'Nunito','Inter', sans-serif"
                fontSize="xs"
                fontWeight="600"
                bg="#25D366"
                _hover={{ bg: "#1ebe5b" }}
                _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              >
                Send on WhatsApp
              </Button>
            ) : null}
          </Flex>
        </Box>

        {selectedSuppliers.length > 0 && (
          <Box
            bg="white"
            borderRadius="xl"
            p={4}
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Text
                fontSize="1rem"
                fontWeight="bold"
                color="gray.800"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Confirmed ({selectedSuppliers.length})
              </Text>
              <Button
                size="xs"
                leftIcon={<Icon as={BsEyeFill} boxSize={4} color="#2966eb" />}
                colorScheme="blue"
                variant="ghost"
                onClick={() => {
                  if (selectedSuppliers.length > 0) {
                    setActiveBucket({
                      id: BucketType.IN_PROGRESS,
                      createdAt: new Date().toISOString(),
                      suppliers: selectedSuppliers,
                      originalParts: originalParts,
                    });
                    setIsDetailsOpen(true);
                  }
                }}
                fontFamily="'Nunito','Inter', sans-serif"
                fontSize="0.75rem"
                fontWeight="700"
              >
                View Details
              </Button>
            </Flex>

            <VStack
              spacing={4}
              align="stretch"
              maxH="27vh"
              overflowY="auto"
              width={"100%"}
              overflowX={"hidden"}
              css={{
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "transparent",
                },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                },
              }}
            >
              {selectedSuppliers.map((supplier, index) => {
                const supplierCPTSlab =
                  supplierCPTSlabs.get(supplier.supplierId) || CPTSlab.OUTSIDE;
                const suggestedCPTSlab = getSuggestedCPTSlab(supplier);

                return (
                  <Box
                    key={supplier.supplierId}
                    bg="#F0F6FF"
                    borderRadius="lg"
                    p={4}
                    position="relative"
                  >
                    {!supplier.isPartConfirmationLocked && (
                      <IconButton
                        aria-label="Remove supplier"
                        icon={<MdClose size="15" />}
                        variant="ghost"
                        colorScheme="red"
                        position="absolute"
                        top={1}
                        right={1}
                        size="2xs"
                        cursor="pointer"
                        onClick={() => onRemoveSupplier(supplier.supplierId)}
                        _hover={{
                          bg: "transparent",
                        }}
                      />
                    )}

                    <VStack align="start" spacing={2} width={"100%"}>
                      <Flex justify="space-between" align="center" width="100%">
                        <VStack align="start" spacing={0} flex={1}>
                          <Text
                            fontSize="0.875rem"
                            fontWeight="bold"
                            color="gray.800"
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            {supplier.supplierName}
                          </Text>
                          <Flex
                            align="center"
                            color="gray.500"
                            gap={1}
                            mt={-0.5}
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            <MdLocationOn
                              size="0.95rem"
                              color={
                                supplier.distanceKm !== undefined &&
                                supplier.distanceKm !== Infinity
                                  ? "#3b82f6"
                                  : "#E40133"
                              }
                            />
                            <Text
                              fontSize="0.7rem"
                              color="gray.600"
                              fontFamily="'Nunito','Inter', sans-serif"
                              mt={0.5}
                            >
                              {supplier.distanceKm !== undefined &&
                              supplier.distanceKm !== Infinity
                                ? `${supplier.distanceKm.toFixed(1)} km away`
                                : "Unavailable"}
                            </Text>
                          </Flex>
                        </VStack>
                        <Badge
                          bg="transparent"
                          color="#3B82F6"
                          alignContent={"right"}
                          borderRadius="full"
                          fontSize="0.9rem"
                          fontWeight="bold"
                          fontFamily="'Nunito','Inter', sans-serif"
                        >
                          #{index + 1}
                        </Badge>
                      </Flex>
                    </VStack>
                  </Box>
                );
              })}
            </VStack>

            <Button
              width="100%"
              mt={2}
              colorScheme="blue"
              variant="solid"
              size="sm"
              fontSize="sm"
              onClick={handleExecuteBucketClick}
              bg="#4b85f2"
              isDisabled={isDisabled || isFetchingDeliveryOptions}
              isLoading={isFetchingDeliveryOptions}
              cursor={
                isDisabled || isFetchingDeliveryOptions
                  ? "not-allowed"
                  : "pointer"
              }
              _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              title={isDisabled ? disableReason : undefined}
            >
              <Tooltip
                label={isDisabled ? disableReason : ""}
                isDisabled={!isDisabled}
                hasArrow
                placement="top"
                bg="gray.700"
                color="white"
                fontSize="sm"
              >
                <span>{isEstimateOrder ? "Generate Estimate" : "Execute"}</span>
              </Tooltip>
            </Button>
          </Box>
        )}
        {isEstimateOrder &&
          onSendPartConfirmation &&
          onRemovePartConfirmationSupplier && (
            <PartConfirmationBucketPanel
              partConfirmationBucket={partConfirmationBucket}
              onRemoveSupplier={onRemovePartConfirmationSupplier}
              onSend={onSendPartConfirmation}
              isSending={isSendingPartConfirmation}
              isSendEnabled={isSendPartConfirmationEnabled}
              sendDisabledReason={sendPartConfirmationDisabledReason}
              sendResults={partConfirmationSendResults}
              nonRemovableSupplierIds={partConfirmationNonRemovableSupplierIds}
            />
          )}
        {bucketsForCurrentOrder.length > 0 && (
          <Box
            bg="white"
            borderRadius="xl"
            p={4}
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text
              fontSize="1rem"
              fontWeight="bold"
              color="gray.800"
              mb={4}
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Already Created Buckets ({bucketsForCurrentOrder.length})
            </Text>
            <VStack
              spacing={4}
              align="stretch"
              maxH="23vh"
              overflowY="auto"
              css={{
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "transparent",
                },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                },
              }}
            >
              {bucketsForCurrentOrder.map((bucket) => (
                <Box
                  key={bucket.id}
                  bg="#F0F6FF"
                  borderRadius="lg"
                  p={3}
                  cursor="pointer"
                  overflow="hidden"
                  onClick={() => {
                    setActiveBucket(bucket);
                    setIsDetailsOpen(true);
                  }}
                >
                  <VStack align="stretch" spacing={2}>
                    <Flex
                      justify="space-between"
                      align="center"
                      gap={2}
                      minW={0}
                    >
                      <Text
                        fontSize="0.875rem"
                        fontWeight="bold"
                        color="gray.800"
                        fontFamily="'Nunito','Inter', sans-serif"
                        noOfLines={1}
                        flex="1"
                        minW={0}
                      >
                        Bucket ({bucket.suppliers.length}{" "}
                        {bucket.suppliers.length === 1
                          ? "Supplier"
                          : "Suppliers"}
                        )
                      </Text>
                      <HStack spacing={1} align="center" flexShrink={0}>
                        <Badge
                          bg="transparent"
                          color="#3B82F6"
                          borderRadius="full"
                          fontSize="0.85rem"
                          fontWeight="bold"
                          fontFamily="'Nunito','Inter', sans-serif"
                          px={1}
                        >
                          <VStack spacing={0} align="center" lineHeight="1">
                            <Text as="span" color="#3B82F6" whiteSpace="nowrap">
                              {
                                calculateRemainingParts(
                                  bucket.originalParts,
                                  bucket.suppliers,
                                ).fulfillmentPercentage
                              }
                              %
                            </Text>
                            <Text
                              as="span"
                              fontSize="0.55rem"
                              color="#3B82F6"
                              whiteSpace="nowrap"
                            >
                              coverage
                            </Text>
                          </VStack>
                        </Badge>
                        <Tooltip
                          label={
                            getDisableReason(orderData, true) ??
                            "Execute Bucket"
                          }
                          hasArrow
                          placement="top"
                          bg="gray.700"
                          color="white"
                          fontSize="sm"
                        >
                          <IconButton
                            aria-label="Execute saved bucket"
                            icon={<FiLogOut size="20" />}
                            variant="ghost"
                            colorScheme="blue"
                            size="2xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              const suppliersWithCPTSlab =
                                calculateCPTSlabsForSuppliers(
                                  orderData,
                                  bucket.suppliers,
                                  hldDistanceMetres,
                                );
                              handleExecuteBucket(suppliersWithCPTSlab);
                            }}
                            _hover={{ bg: "transparent" }}
                            isDisabled={
                              !orderData?.orderETA?.length ||
                              !orderData.orderHandler?.length
                            }
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="Delete saved bucket"
                          icon={<MdDeleteForever size="20" />}
                          variant="ghost"
                          colorScheme="red"
                          size="2xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBucketByBucketId(orderNumber, bucket.id);
                          }}
                          _hover={{ bg: "transparent" }}
                        />
                      </HStack>
                    </Flex>
                    {showDualBucketDeliveryTypeUi && (
                      <RadioGroup
                        value={bucketDeliveryTypes[bucket.id] || ""}
                        onChange={(value) => {
                          if (value === "FASTEST" || value === "CHEAPEST") {
                            handleBucketDeliveryTypeChange(bucket.id, value);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <HStack spacing={4}>
                          <Radio
                            value="FASTEST"
                            size="sm"
                            colorScheme="blue"
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            Fastest
                          </Radio>
                          <Radio
                            value="CHEAPEST"
                            size="sm"
                            colorScheme="blue"
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            Cheapest
                          </Radio>
                        </HStack>
                      </RadioGroup>
                    )}
                  </VStack>
                </Box>
              ))}
            </VStack>
            {showDualBucketDeliveryTypeUi && (
              <Tooltip
                label={
                  !orderData?.orderETA?.length
                    ? "ETA is missing for this order"
                    : !orderData?.orderHandler?.length
                      ? "Order handler not assigned"
                      : ""
                }
                isDisabled={
                  Boolean(orderData?.orderETA?.length) &&
                  Boolean(orderData?.orderHandler?.length)
                }
                hasArrow
                placement="top"
                bg="gray.700"
                color="white"
                fontSize="sm"
              >
                <Button
                  width="100%"
                  mt={3}
                  colorScheme="blue"
                  variant="solid"
                  size="sm"
                  fontSize="sm"
                  onClick={handleGlobalViewEstimateClick}
                  bg="#4b85f2"
                  isDisabled={
                    !orderData?.orderETA?.length ||
                    !orderData?.orderHandler?.length ||
                    isFetchingDeliveryOptions ||
                    !bucketDeliveryTypes[bucketsForCurrentOrder[0]?.id] ||
                    !bucketDeliveryTypes[bucketsForCurrentOrder[1]?.id]
                  }
                  isLoading={isFetchingDeliveryOptions}
                  _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
                >
                  Generate Estimate
                </Button>
              </Tooltip>
            )}
          </Box>
        )}
      </VStack>

      <SavedBucketDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setActiveBucket(null);
        }}
        bucket={activeBucket}
      />
    </Box>
  );
};
